import type {
    PresetMap,
    BaseViz,
    ItemShouldAnnounceChangeFunction,
} from '@splunk/dashboard-types';

// ??? visualizations
import ConnectedLine from '@splunk/dashboard-layouts/visualizations/ConnectedLine';
// New Visualizations
import PlatformArea from '@splunk/visualizations/Area';
import PlatformBar from '@splunk/visualizations/Bar';
import PlatformBubble from '@splunk/visualizations/Bubble';
import PlatformChoroplethSvg from '@splunk/visualizations/ChoroplethSvg';
import PlatformColumn from '@splunk/visualizations/Column';
import PlatformEllipse from '@splunk/visualizations/Ellipse';
import PlatformEvents from '@splunk/visualizations/Events';
import PlatformFillerGauge from '@splunk/visualizations/FillerGauge';
import PlatformImage from '@splunk/visualizations/Image';
import PlatformLine from '@splunk/visualizations/Line';
import PlatformLinkGraph from '@splunk/visualizations/LinkGraph';
import PlatformMap from '@splunk/visualizations/Map';
import PlatformMarkdown from '@splunk/visualizations/Markdown';
import PlatformMarkerGauge from '@splunk/visualizations/MarkerGauge';
import PlatformParallelCoordinates from '@splunk/visualizations/ParallelCoordinates';
import PlatformNetworkGraph from '@splunk/visualizations/NetworkGraph';
import PlatformPie from '@splunk/visualizations/Pie';
import PlatformPunchcard from '@splunk/visualizations/Punchcard';
import PlatformRectangle from '@splunk/visualizations/Rectangle';
import PlatformSankey from '@splunk/visualizations/Sankey';
import PlatformScatter from '@splunk/visualizations/Scatter';
import PlatformSingleValue from '@splunk/visualizations/SingleValue';
import PlatformSingleValueIcon from '@splunk/visualizations/SingleValueIcon';
import PlatformSingleValueRadial from '@splunk/visualizations/SingleValueRadial';
import PlatformTable from '@splunk/visualizations/Table';
import PlatformTimeline from '@splunk/visualizations/Timeline';

import { isEqual } from 'lodash';
import {
    withCustomUrl,
    withDragHandle,
    withSetTokens,
    withToolbar,
    withVizSwitcher,
    withShowCornerRadiusEditor,
    withShowDrilldown,
    withCommonCapabilities,
    withSwitchToTab,
    withSetTimeRange,
    withShowSkipButton,
    withHideVisibilitySection,
    withUnsetTokens,
    withCustomShouldAnnounceChange,
    withIcon,
} from './utils';

type PlatformViz = typeof PlatformTable;

const platformVisualizations: PresetMap['visualizations'] = {};
// Add shapes and shape-like special platform visualizations (no title/ descriptions, progress bar, last updated, no toolbar or switcher)
[
    ConnectedLine as unknown as BaseViz,
    PlatformEllipse,
    PlatformImage,
    PlatformMarkdown,
    PlatformRectangle,
    PlatformSingleValueIcon,
].forEach((vizType) => {
    platformVisualizations[(vizType as PlatformViz).config.key] = vizType;
});

// Add default platform visualizations (defaults in toolbar and switcher)
// Order matters! It dictates the order of the visualizations in their respective viz.category
[
    PlatformArea,
    PlatformBar,
    PlatformBubble,
    PlatformChoroplethSvg,
    PlatformColumn,
    PlatformTable,
    PlatformEvents,
    PlatformFillerGauge,
    PlatformLine,
    PlatformLinkGraph,
    PlatformMap,
    PlatformMarkerGauge,
    PlatformParallelCoordinates,
    PlatformNetworkGraph,
    PlatformPie,
    PlatformPunchcard,
    PlatformSankey,
    PlatformScatter,
    PlatformSingleValue,
    PlatformSingleValueRadial,
    PlatformTimeline,
].forEach((vizType) => {
    platformVisualizations[vizType.config.key] = withCommonCapabilities(
        withVizSwitcher(withToolbar(vizType))
    );
});

// Enhance platform visualization to support setting of dynamic and static tokens through event drilldowns
[
    PlatformArea,
    PlatformBar,
    PlatformBubble,
    PlatformChoroplethSvg,
    PlatformColumn,
    PlatformEllipse,
    PlatformEvents,
    PlatformLine,
    PlatformLinkGraph,
    PlatformMap,
    PlatformParallelCoordinates,
    PlatformNetworkGraph,
    PlatformPie,
    PlatformPunchcard,
    PlatformRectangle,
    PlatformSankey,
    PlatformScatter,
    PlatformSingleValue,
    PlatformSingleValueIcon,
    PlatformSingleValueRadial,
    PlatformTable,
    PlatformTimeline,
].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key];
    platformVisualizations[vizType.config.key] = withSetTokens(viz);
});

[ConnectedLine].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key];
    platformVisualizations[vizType.config.key] = withHideVisibilitySection(viz);
});

// Enhance platform visualization to support setting of static tokens through event drilldowns
[PlatformImage, PlatformMarkdown].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key];
    platformVisualizations[vizType.config.key] = withSetTokens(viz, ['static']);
});

// Enhance platform visualization to support setting of timerange tokens through event drilldowns
[PlatformArea, PlatformColumn, PlatformLine].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key];
    platformVisualizations[vizType.config.key] = withSetTimeRange(viz);
});

// Enhance platform visualization to support redirecting to custom url through event drilldowns
[
    PlatformArea,
    PlatformBar,
    PlatformBubble,
    PlatformChoroplethSvg,
    PlatformColumn,
    PlatformEllipse,
    PlatformImage,
    PlatformLine,
    PlatformLinkGraph,
    PlatformMap,
    PlatformMarkdown,
    PlatformNetworkGraph,
    PlatformPie,
    PlatformPunchcard,
    PlatformRectangle,
    PlatformScatter,
    PlatformSingleValue,
    PlatformSingleValueIcon,
    PlatformSingleValueRadial,
    PlatformTable,
    PlatformTimeline,
].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key];
    platformVisualizations[vizType.config.key] = withCustomUrl(viz);
    platformVisualizations[vizType.config.key] = withSwitchToTab(viz);
    platformVisualizations[vizType.config.key] = withUnsetTokens(viz);
});

// Enhance platform visualization to show drag handle
[PlatformMap, PlatformNetworkGraph].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key];
    platformVisualizations[vizType.config.key] = withDragHandle(viz);
});

// drilldown deny list
const hideDrilldownList = [
    'abslayout.line',
    'splunk.fillergauge',
    'splunk.markergauge',
    'splunk.parallelcoordinates',
    'splunk.events',
];

// apply showDrilldown to all visualizations except the ones in the denyList
Object.entries(platformVisualizations).forEach(([key, Viz]) => {
    if (hideDrilldownList.includes(key)) {
        return;
    }
    platformVisualizations[key] = withShowDrilldown(Viz);
});

// show skip button only for visualizations from the list
const showSkipButtonList = ['splunk.events'];
Object.entries(platformVisualizations).forEach(([key, Viz]) => {
    if (!showSkipButtonList.includes(key)) {
        return;
    }
    platformVisualizations[key] = withShowSkipButton(Viz);
});

// hardcode icons for visualizations that don't have icons in config
// stopgap till SCP-80375 is done
[
    PlatformSingleValueIcon,
    PlatformMarkdown,
    PlatformRectangle,
    PlatformEllipse,
].forEach((vizType) => {
    const viz = platformVisualizations[vizType.config.key]!;
    platformVisualizations[vizType.config.key] = withIcon(viz, {
        key: vizType.config.key,
    });
});

export const markdownVisualizationHasChanged: ItemShouldAnnounceChangeFunction =
    ({ newDefinition, previousDefinition }) => {
        const currOptions = newDefinition?.options;
        const prevOptions = previousDefinition?.options;

        return !isEqual(currOptions, prevOptions);
    };

platformVisualizations['splunk.markdown'] = withCustomShouldAnnounceChange(
    platformVisualizations['splunk.markdown'],
    {
        shouldAnnounceChange: markdownVisualizationHasChanged,
    }
);

// Show a cornerRadius editor for all visualizations except those in this list
const hideCornerRadiusEditor = [
    'abslayout.line',
    'splunk.ellipse',
    'splunk.rectangle',
];
Object.entries(platformVisualizations).forEach(([key, Viz]) => {
    if (hideCornerRadiusEditor.includes(key)) {
        return;
    }
    platformVisualizations[key] = withShowCornerRadiusEditor(Viz);
});

export default {
    visualizations: platformVisualizations,
};
